export const useFormat = () => {
  const formatNumber = (num?: number) =>
    num !== undefined
      ? num % 1 === 0
        ? num.toLocaleString()
        : num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      : undefined

  const formatLastFourDigits = (num?: number | string) => {
    if (num === undefined) return undefined

    return num.toString().slice(-4)
  }

  function formatPhoneNumber(input?: number | string): string | undefined {
    if (input === undefined) return undefined

    const digits = input.toString().replace(/\D/g, '')

    // If there are less than 10 digits, return the cleaned number as-is.
    if (digits.length < 10) return digits

    // The last 10 digits are considered the local number (area code, central office code, and line number)
    const localNumber = digits.slice(-10)
    const areaCode = localNumber.slice(0, 3)
    const centralOffice = localNumber.slice(3, 6)
    const lineNumber = localNumber.slice(6)

    // Any extra digits at the beginning are treated as the country code.
    const countryCode = digits.slice(0, -10)

    // Build the formatted phone number string.
    const formattedLocal = `(${areaCode}) ${centralOffice}-${lineNumber}`

    return countryCode ? `+${countryCode} ${formattedLocal}` : formattedLocal
  }

  type FormatOptions = {
    minimumFractionDigits?: number
    maximumFractionDigits?: number
  }

  const formatterCache = new Map<string, Intl.NumberFormat>()

  function getFormatter(opts: FormatOptions = {}) {
    const key = `${opts.minimumFractionDigits ?? 0}-${opts.maximumFractionDigits ?? 2}`

    if (!formatterCache.has(key)) {
      formatterCache.set(
        key,
        new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: opts.minimumFractionDigits ?? 0,
          maximumFractionDigits: opts.maximumFractionDigits ?? 2
        })
      )
    }

    return formatterCache.get(key)!
  }

  const formatUSD = (value?: number | string, opts?: FormatOptions): string | undefined => {
    if (value === undefined || value === null) return undefined

    const num = typeof value === 'string' ? Number(value) : value

    if (!Number.isFinite(num)) return undefined

    return getFormatter(opts).format(num)
  }

  const convertToMBOrKB = (bytes: number): string => {
    const MB = bytes / (1024 * 1024)
    if (MB < 1) {
      return (MB * 1024).toFixed(0).toString() + 'KB'
    }
    return MB.toFixed(0).toString() + 'MB'
  }

  return { formatNumber, formatLastFourDigits, formatPhoneNumber, formatUSD, convertToMBOrKB }
}
